import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import BookingStatusBadge from "@/components/BookingStatusBadge";
import { Send, Loader2, ArrowLeft, MoreVertical, Flag, Ban, MessageSquare, Check, CheckCheck, Archive } from "lucide-react";
import { toast } from "sonner";

export default function Messages() {
  const { user } = useAuth();
  const location = useLocation();
  const [convos, setConvos] = useState([]);
  const [active, setActive] = useState(location.state?.to || null);
  const [activeName, setActiveName] = useState(location.state?.name || "");
  const [messages, setMessages] = useState([]);
  const [booking, setBooking] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const [menu, setMenu] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const scrollRef = useRef(null);

  const loadConvos = async () => {
    const { data } = await api.get("/messages/conversations");
    setConvos(data.conversations);
    setLoading(false);
  };
  useEffect(() => { loadConvos(); }, []);

  useEffect(() => {
    if (!active) return;
    const load = async () => {
      const { data } = await api.get(`/messages/${active}`);
      setMessages(data.messages);
      setBooking(data.booking || null);
      setBlocked(!!data.blocked);
      if (data.other?.name) setActiveName(data.other.name);
    };
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [active]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages]);

  const send = async () => {
    if (!text.trim() || !active) return;
    const t = text.trim();
    setText("");
    try {
      await api.post("/messages", { recipient_id: active, text: t, booking_id: booking?.booking_id });
      const { data } = await api.get(`/messages/${active}`);
      setMessages(data.messages);
      loadConvos();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't send message");
    }
  };

  const report = async () => {
    setMenu(false);
    await api.post("/messages/report", { reported_user_id: active, reason: "inappropriate" });
    toast.success("Thanks — our team will review this conversation.");
  };
  const block = async () => {
    setMenu(false);
    await api.post(`/messages/block/${active}`);
    setBlocked(true);
    toast.success("User blocked. You can unblock anytime.");
  };
  const unblock = async () => {
    await api.delete(`/messages/block/${active}`);
    setBlocked(false);
    toast.success("User unblocked.");
  };
  const archived = convos.find((c) => c.other_id === active)?.archived;
  const toggleArchive = async () => {
    setMenu(false);
    await api.post(`/messages/${active}/archive?archive=${!archived}`);
    toast.success(!archived ? "Conversation archived" : "Conversation restored");
    loadConvos();
  };

  const shownConvos = convos.filter((c) => (showArchived ? c.archived : !c.archived));

  const initials = (n) => (n || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 sm:py-8">
        <h1 className="font-display text-2xl sm:text-3xl font-medium tracking-tight mb-5">Messages</h1>
        <div className="grid md:grid-cols-[300px_1fr] gap-5 h-[70vh] min-h-[520px]">
          {/* Conversation list */}
          <div className={`bg-card rounded-2xl border border-primary/5 overflow-y-auto ${active ? "hidden md:block" : "block"}`} data-testid="conversation-list">
            <div className="sticky top-0 bg-card border-b border-border p-2 flex gap-2 z-10">
              <button onClick={() => setShowArchived(false)} className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition-colors ${!showArchived ? "bg-secondary" : "hover:bg-secondary/50"}`} data-testid="tab-active-convos">Active</button>
              <button onClick={() => setShowArchived(true)} className={`flex-1 py-1.5 rounded-lg text-sm font-medium transition-colors ${showArchived ? "bg-secondary" : "hover:bg-secondary/50"}`} data-testid="tab-archived-convos">Archived</button>
            </div>
            {loading ? <div className="p-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-accent" /></div> :
              shownConvos.length === 0 ? (
                <div className="p-8 text-center" data-testid="empty-conversations">
                  <MessageSquare className="h-10 w-10 mx-auto text-muted-foreground/50" />
                  <p className="mt-3 font-medium">{showArchived ? "No archived chats" : "No conversations yet"}</p>
                  <p className="text-sm text-muted-foreground mt-1">{showArchived ? "Completed conversations you archive will appear here." : "Message a cleaner from their profile or one of your bookings to start chatting."}</p>
                </div>
              ) :
              shownConvos.map((c) => (
                <button key={c.conversation_id} onClick={() => { setActive(c.other_id); setActiveName(c.other_name); }} className={`w-full text-left px-4 py-3 border-b border-border hover:bg-secondary/50 transition-colors flex items-center gap-3 ${active === c.other_id ? "bg-secondary/50" : ""}`} data-testid={`convo-${c.other_id}`}>
                  <span className="h-10 w-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold shrink-0">{initials(c.other_name)}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium truncate">{c.other_name}</span>
                      {c.unread > 0 && <span className="h-5 w-5 rounded-full bg-accent text-white text-xs flex items-center justify-center shrink-0">{c.unread}</span>}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{c.last_message}</p>
                    {c.booking && <p className="text-xs text-accent truncate">Re: {c.booking.service_name}</p>}
                  </div>
                </button>
              ))}
          </div>

          {/* Thread */}
          <div className={`bg-card rounded-2xl border border-primary/5 flex flex-col ${active ? "flex" : "hidden md:flex"}`}>
            {active ? (
              <>
                <div className="px-4 py-3 border-b border-border flex items-center gap-3">
                  <button onClick={() => setActive(null)} className="md:hidden p-1" data-testid="messages-back"><ArrowLeft className="h-5 w-5" /></button>
                  <span className="h-9 w-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-semibold">{initials(activeName)}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-semibold truncate">{activeName || "Conversation"}</p>
                    {booking && (
                      <div className="flex items-center gap-2" data-testid="booking-context">
                        <span className="text-xs text-muted-foreground truncate">Re: {booking.service_name} · {booking.date}</span>
                        <BookingStatusBadge status={booking.status} />
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <button onClick={() => setMenu(!menu)} className="p-2 rounded-full hover:bg-secondary transition-colors" data-testid="msg-menu"><MoreVertical className="h-5 w-5" /></button>
                    {menu && (
                      <div className="absolute right-0 mt-1 w-44 bg-card border border-border rounded-xl shadow-lg overflow-hidden z-10">
                        <button onClick={report} className="w-full text-left px-4 py-2.5 text-sm hover:bg-secondary flex items-center gap-2" data-testid="msg-report"><Flag className="h-4 w-4" /> Report</button>
                        <button onClick={toggleArchive} className="w-full text-left px-4 py-2.5 text-sm hover:bg-secondary flex items-center gap-2" data-testid="msg-archive"><Archive className="h-4 w-4" /> {archived ? "Unarchive" : "Archive"}</button>
                        {blocked ? (
                          <button onClick={unblock} className="w-full text-left px-4 py-2.5 text-sm hover:bg-secondary flex items-center gap-2" data-testid="msg-unblock"><Ban className="h-4 w-4" /> Unblock</button>
                        ) : (
                          <button onClick={block} className="w-full text-left px-4 py-2.5 text-sm text-destructive hover:bg-secondary flex items-center gap-2" data-testid="msg-block"><Ban className="h-4 w-4" /> Block</button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div ref={scrollRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                  {messages.length === 0 ? (
                    <p className="text-center text-sm text-muted-foreground mt-10">No messages yet. Say hello 👋</p>
                  ) : messages.map((m) => (
                    <div key={m.message_id} className={`flex ${m.sender_id === user.user_id ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${m.sender_id === user.user_id ? "bg-primary text-primary-foreground rounded-br-sm" : "bg-secondary rounded-bl-sm"}`}>
                        {m.text}
                        <span className={`flex items-center gap-1 text-[10px] mt-1 ${m.sender_id === user.user_id ? "text-primary-foreground/60 justify-end" : "text-muted-foreground"}`}>
                          {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                          {m.sender_id === user.user_id && (m.read ? <CheckCheck className="h-3 w-3" data-testid="receipt-read" /> : <Check className="h-3 w-3" data-testid="receipt-sent" />)}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>

                {blocked ? (
                  <div className="p-4 border-t border-border text-center text-sm text-muted-foreground">You've blocked this user. Unblock to send messages.</div>
                ) : (
                  <div className="p-3 border-t border-border flex gap-2">
                    <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Type a message…" className="flex-1 px-4 py-2.5 rounded-full bg-background border border-border outline-none focus:border-accent" data-testid="message-input" />
                    <button onClick={send} className="h-10 w-10 rounded-full bg-accent text-white flex items-center justify-center hover:bg-accent/90 transition-colors shrink-0" data-testid="message-send"><Send className="h-4 w-4" /></button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center text-muted-foreground p-8 text-center">
                <MessageSquare className="h-12 w-12 text-muted-foreground/40" />
                <p className="mt-3 font-medium">Select a conversation</p>
                <p className="text-sm">Your messages with cleaners and customers appear here.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
